import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

export function Accordion({ children, type = "single", collapsible = false, className = "" }) {
  const [openItems, setOpenItems] = useState(new Set());

  const handleToggle = (value) => {
    setOpenItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(value)) {
        if (collapsible) {
          newSet.delete(value);
        }
      } else {
        if (type === "single") {
          newSet.clear();
        }
        newSet.add(value);
      }
      return newSet;
    });
  };

  return (
    <div className={className}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            isOpen: openItems.has(child.props.value),
            onToggle: handleToggle,
          });
        }
        return child;
      })}
    </div>
  );
}

export function AccordionItem({ children, value, className = "", isOpen, onToggle, ...props }) {
  return (
    <div className={className} {...props}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            value,
            isOpen,
            onToggle,
          });
        }
        return child;
      })}
    </div>
  );
}

export function AccordionTrigger({ children, className = "", value, isOpen, onToggle, ...props }) {
  const handleClick = () => {
    if (onToggle && value) {
      onToggle(value);
    }
  };

  return (
    <button
      type="button"
      className={`flex w-full items-center justify-between py-4 font-medium transition-all hover:no-underline ${className}`}
      onClick={handleClick}
      aria-expanded={isOpen}
      {...props}
    >
      <span>{children}</span>
      <ChevronDown
        className={`h-4 w-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
  );
}

// value destructured to omit from ...props (no-unused-vars disabled intentionally)
// eslint-disable-next-line no-unused-vars
export function AccordionContent({ children, className = "", value, isOpen, ...props }) {
  return (
    <div
      className={`overflow-hidden transition-all duration-200 ${
        isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      } ${className}`}
      {...props}
    >
      <div className="pb-4 pt-0">
        {children}
      </div>
    </div>
  );
}
